/**
 * Chat data kept in the browser between page loads, so the chat list and the
 * last few threads paint instantly after a reload instead of waiting on the
 * server. Everything shown from here is refreshed from the server straight
 * away; this only decides what is on screen for the first second.
 *
 * localStorage rather than memory, keyed by user, and wiped on logout and on
 * an expired session (clearChatStore). The login token already lives in
 * localStorage, so anyone at an unattended browser can open the portal anyway;
 * the recent chats kept here do not widen that. Entries older than a day are
 * ignored rather than shown.
 */

const PREFIX = 'gts.chat.';
const MAX_AGE_MS = 24 * 60 * 60 * 1000;

const keyFor = (userId, name) => `${PREFIX}${userId}.${name}`;

export const readChatStore = (userId, name) => {
    if (!userId) return null;
    try {
        const raw = localStorage.getItem(keyFor(userId, name));
        if (!raw) return null;
        const { at, value } = JSON.parse(raw);
        if (!at || Date.now() - at > MAX_AGE_MS) return null;
        return value;
    } catch {
        return null;
    }
};

export const writeChatStore = (userId, name, value) => {
    if (!userId) return;
    try {
        localStorage.setItem(keyFor(userId, name), JSON.stringify({ at: Date.now(), value }));
    } catch {
        // Storage full or blocked (private mode): the page works without it.
    }
};

/** Forget every user's saved chats. Called wherever the session ends. */
export const clearChatStore = () => {
    try {
        Object.keys(localStorage)
            .filter((key) => key.startsWith(PREFIX))
            .forEach((key) => localStorage.removeItem(key));
    } catch {
        // Nothing to clear.
    }
};
